import { useState } from 'react';
import { createClient, searchClientByPhone } from '../firebase/clientService';
import { isValidSouthAfricanPhone } from '../utils/validation';
import LogoutButton from '../components/auth/LogoutButton';
import { useAuth } from '../context/AuthContext';

function NewClientForm({ phoneNumber, onComplete, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    phoneNumber: phoneNumber
  });
  const [phoneError, setPhoneError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();

  const validatePhone = (phone) => {
    if (!phone) {
      setPhoneError('Phone number is required');
      return false;
    }
    if (!isValidSouthAfricanPhone(phone)) {
      setPhoneError('Invalid SA mobile number. Use format 0721234567');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const handlePhoneChange = (e) => {
    const newPhone = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phoneNumber: newPhone });
    validatePhone(newPhone);
  };

  const checkPhoneExists = async () => {
    if (!validatePhone(formData.phoneNumber)) return true;
    const existing = await searchClientByPhone(formData.phoneNumber);
    if (existing) {
      setError(`Client with phone number ${formData.phoneNumber} already exists. Use search instead.`);
      return true;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) {
      setError('Please enter client name');
      return;
    }
    if (!validatePhone(formData.phoneNumber)) {
      setError('Please enter a valid SA mobile number');
      return;
    }
    const phoneExists = await checkPhoneExists();
    if (phoneExists) return;
    setSaving(true);
    try {
      const client = await createClient(formData);
      setSaving(false);
      onComplete(client);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-salon-bg">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
           <img src="/images/abbr-grey.png" alt="Muse & Mane" className="h-10" />
            <span className="font-semibold text-gray-800">Salon Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">{currentUser?.email}</span>
            <LogoutButton />
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-center min-h-[calc(100vh-73px)]">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md border border-salon-border">
          {error && <div className="bg-red-100 text-red-700 p-3 rounded-md mb-4">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-salon-button" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input type="tel" value={formData.phoneNumber} onChange={handlePhoneChange} placeholder="0721234567" className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${phoneError ? 'border-red-500' : 'border-gray-300 focus:ring-salon-button'}`} />
              {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
              <p className="text-gray-400 text-xs mt-1">South African mobile number (10 digits, starts with 0, second digit 6-8)</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-salon-button" />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
              <input type="number" value={formData.age} onChange={(e) => setFormData({...formData, age: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-salon-button" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onCancel} className="flex-1 bg-gray-200 py-2 rounded-md hover:bg-gray-300 transition">Cancel</button>
              <button type="submit" disabled={saving || !!phoneError} className="flex-1 bg-salon-button text-white py-2 rounded-md hover:bg-salon-button-hover disabled:opacity-50 transition">{saving ? 'Creating...' : 'Continue'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default NewClientForm;